/**
 * ShotLatencyCard Component
 *
 * Displays a color-coded shot latency status indicator card.
 * Shows tier classification, relative time, and tooltip with exact details.
 *
 * Requirements: 3.1, 3.2, 3.3, 3.4, 3.5, 3.6, 3.7, 4.1, 4.2, 5.1, 5.2, 6.1, 6.2, 6.3, 7.1, 7.2, 7.3
 */

import { useShotLatency } from '../hooks/useShotLatency';
import { classifyLatency, formatImpactTime } from '../utils/latencyClassifier';
import './ShotLatencyCard.css';

interface ShotLatencyCardProps {
  readonly siteId: string;
}

export function ShotLatencyCard({ siteId }: ShotLatencyCardProps): JSX.Element {
  const { data, isLoading, isNoData, error } = useShotLatency(siteId);

  return (
    <div className="site-overview__card">
      <h3 className="site-overview__card-title">Shot Latency</h3>
      {renderContent()}
    </div>
  );

  function renderContent(): JSX.Element {
    if (isLoading) {
      return <p className="shot-latency-card__loading">Loading…</p>;
    }

    if (error) {
      return <p className="shot-latency-card__error">{error}</p>;
    }

    if (isNoData || !data) {
      return (
        <div className="shot-latency-card__content">
          <span
            className="shot-latency-card__indicator shot-latency-card__indicator--no-data"
            role="img"
            aria-label="No latency data available"
          />
          <span className="shot-latency-card__no-data">No data</span>
        </div>
      );
    }

    const { tier, color } = classifyLatency(data.latencyMs);
    const formattedTime = formatImpactTime(data.impactTime);
    const tooltipText = `${data.latencyMs} ms — Shot ID: ${data.shotId}`;

    return (
      <div title={tooltipText}>
        <div className="shot-latency-card__content">
          <span
            className="shot-latency-card__indicator"
            role="img"
            aria-label={`Latency status: ${tier}`}
            style={{ backgroundColor: color }}
          />
          <p className="shot-latency-card__tier">{tier}</p>
        </div>
        <p className="shot-latency-card__time">{formattedTime}</p>
      </div>
    );
  }
}
